import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { ASSETS_URL, isDevelopment } from "@/config/deployConstants";
import br from "@/styles/breakpoints.module.css";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { report } from "@/utils/analytics/logging";
import { themeColors } from "@/utils/themeColors";
import { authSafe, emailFromUsername, signIn } from "@knowt/syncing/hooks/user/auth";
import { callManuallyVerifyUser } from "@knowt/syncing/hooks/user/graphqlUtils";
import { autoSignIn, confirmSignUp, resendSignUpCode } from "aws-amplify/auth";
import clsx from "clsx";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "@bprogress/next";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useAuthSlideContextSelector } from "../AuthSlide/hook/useAuthSlideContext";
import styles from "../auth.module.css";
import AuthKnowtLogo from "../components/AuthKnowtLogo";
import {
    AuthLeftContainer,
    AuthLeftInnerContainer,
    AuthMainContainer,
    AuthRightContainer,
} from "../components/styled/Containers";
import { AuthForm, AuthInputCode, AuthSubmitButton } from "../components/styled/Forms";
import { GapPerSection } from "../components/styled/Separator";
import { SS_MANUALLY_VERIFY_SIGNIN_KEY } from "../utils";
import passwordAnimationDark from "@/public/images/lottie/sign-up-whats-the-password-dark.json";
import passwordAnimationLight from "@/public/images/lottie/sign-up-whats-the-password-light.json";
import { useThemeContextSelector } from "@/utils/theme";

const HelpButton = ({ img, label, onClick }: { img: string; label: string; onClick: () => void }) => {
    return (
        <FlexColumn>
            <CircularRectTextButton
                type="button"
                onClick={onClick}
                className="tertiaryTextBold1"
                sx={{
                    height: "4rem",
                    backgroundColor: "transparent",
                    border: `1px solid ${themeColors.neutral1}`,
                    gap: "1.1rem",
                    "&:hover": {
                        background: themeColors.neutralBlack,
                        color: themeColors.neutralWhite,
                    },
                }}>
                <Image src={img} alt="label" width={20} height={20} style={{ objectFit: "contain" }} />
                {label}
            </CircularRectTextButton>
        </FlexColumn>
    );
};

const VerifyPage = ({
    onVerified,
    onLoadVerifyError,
}: {
    onVerified?: () => Promise<void>;
    onLoadVerifyError: () => void;
}) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);
    const closeAuthSlide = useAuthSlideContextSelector(state => state.closeAuthSlide);
    const [authStatus, setAuthStatus] = useState<"loading" | "success" | "failure" | null>(null);

    const router = useRouter();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const username = searchParams?.get("username") || "";
    const fromSignup = searchParams?.get("fromSignup");

    // route user if try to access verify page without username query
    // biome-ignore lint: noExhaustiveDependencies
    useEffect(() => {
        if (!username && pathname === "/verify") {
            toast.error("An error has occurred on verifying");

            if (onLoadVerifyError) {
                onLoadVerifyError();
            } else {
                router.push("/home");
            }
        }
    }, []);

    const [code, setCode] = useState("");

    // biome-ignore lint: useExhaustiveDependencies
    useEffect(() => {
        if (!fromSignup && username) {
            sendCode();
        }
    }, []);

    const sendCode = () => {
        authSafe(a => resendSignUpCode({ username: a }))(username)
            .then(() => {
                toast.success("We sent the code again! Check your email.");
            })
            .catch(err => {
                report(err, "sendCode");
                toast.error("Something happened, please try again.");
            });
    };

    const verifyUser = () => {
        if (authStatus === "loading" || authStatus === "success") return;

        if (code.length < 3) {
            toast.error("Enter your verification code.");
            return;
        }
        setAuthStatus("loading");
        authSafe((a, b) => confirmSignUp({ username: a, confirmationCode: b }))(username, code.trim())
            .then(async () => {
                setAuthStatus("success");
                await autoSignIn();
                await onVerified?.();
            })
            .catch(err => {
                if (err.name === "CodeMismatchException") {
                    setAuthStatus("failure");
                    toast.error("The code entered is not correct.");
                    setTimeout(() => setAuthStatus(null), 500);
                } else if (err.name === "NotAuthorizedException") {
                    setAuthStatus("failure");
                    toast.error("Your account is already verified. Please try to login instead.");
                    setTimeout(() => setAuthStatus(null), 500);
                } else {
                    report(err, "verifyUser");
                }
            });
    };

    const resendCode = () => {
        authSafe(a => resendSignUpCode({ username: a }))(username);
        Mixpanel.track("Verify Account - Resend Clicked", {
            username: username,
            email: emailFromUsername(username),
        });
        toast.success("Sent another verification email!");
    };

    const manuallyVerifyAndSignIn = async () => {
        if (isDevelopment) {
            await manuallyVerifyAndSignInDEV();
        } else {
            closeAuthSlide();
            router.push(`/verify/error?username=${username}`);
        }
    };

    // ONLY ON DEV
    const manuallyVerifyAndSignInDEV = async () => {
        toast.loading("Verifying your account...", { id: "MANUAL_VERIFY_TOAST" });
        await authSafe(a => callManuallyVerifyUser(a))(username);

        Mixpanel.track("Verify Account - Manually Verified", {
            username,
            email: emailFromUsername(username),
        });

        const { username: amplifyUsername, password } = JSON.parse(
            window?.sessionStorage.getItem(SS_MANUALLY_VERIFY_SIGNIN_KEY) || "{}"
        );

        if (!amplifyUsername || !password) {
            toast.success("Your account has been verified. Please login now!", { id: "MANUAL_VERIFY_TOAST" });
            window.location.href = "/login";
            return;
        } else {
            toast.success("Account verified!", { id: "MANUAL_VERIFY_TOAST" });
        }

        await signIn(amplifyUsername, password);
        setAuthStatus("success");
        onVerified?.();
    };

    const verifyCardsItem = [
        {
            label: "Re-send the verify email",
            image: `${ASSETS_URL}/images/emailVerif.png`,
            onClick: () => resendCode(),
        },
        {
            label: "Still didn't get an email? Contact us",
            image: `${ASSETS_URL}/images/helpVerif.png`,
            onClick: () => manuallyVerifyAndSignIn(),
        },
    ];

    const renderHelpButtons = () => {
        return (
            <FlexColumn style={{ gap: "1.6rem" }}>
                {verifyCardsItem.map((item, index) => (
                    <HelpButton key={index + item.label} img={item.image} label={item.label} onClick={item.onClick} />
                ))}
            </FlexColumn>
        );
    };

    const handleClose = () => {
        router.push("/home");
        closeAuthSlide();
    };

    return (
        <AuthMainContainer>
            <AuthLeftContainer>
                <AuthKnowtLogo onClose={handleClose} />
                <AuthLeftInnerContainer style={{ margin: "auto 0" }}>
                    <h2 className={clsx("heading4")} style={{ marginBottom: "1rem" }}>
                        Type the code we sent to {!username ? "..." : username?.replace("@-@", "@")}
                    </h2>
                    <p className={"body2"} style={{ color: themeColors.neutral3 }}>
                        Please check your spam if you don’t see it.
                    </p>

                    <GapPerSection height={"4rem"} />

                    <AuthForm onSubmit={verifyUser}>
                        <FlexRowAlignCenter style={{ gap: "1.3rem", margin: "auto", width: "fit-content" }}>
                            <AuthInputCode
                                className={styles.authCodeInputScale}
                                length={6}
                                onChange={newCode => setCode(newCode)}
                                style={{ border: "none", backgroundColor: themeColors.background }}
                            />
                        </FlexRowAlignCenter>
                        <GapPerSection height={"4rem"} />
                        <AuthSubmitButton authStatus={authStatus}>Verify</AuthSubmitButton>
                    </AuthForm>

                    <GapPerSection height={"4rem"} />
                    {renderHelpButtons()}
                    <div className={br.smUpDisplayNone} style={{ paddingBottom: "5rem" }} />
                </AuthLeftInnerContainer>
            </AuthLeftContainer>

            <AuthRightContainer
                onClose={handleClose}
                animation={isDarkMode ? passwordAnimationDark : passwordAnimationLight}
            />
        </AuthMainContainer>
    );
};

export default VerifyPage;
